import React, { useState, useMemo, useEffect, useContext } from 'react';
import { CaretDown, Buildings, Sun, BatteryHigh, House, Link, Copy } from '@phosphor-icons/react';
import { message } from 'antd';
import { useOneButtonStore, COMMON_CONSTANTS, type PartnerSyncChecklist } from '../../../../store/oneButtonStore';
import { isPartnerSyncCompleteForConfig } from './partnerSyncConfig';
import FinancePartnerChecklistModal from './FinancePartnerChecklistModal';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import opportunityInfoServices from '../../../../services/oneButtonServices/opportunityInfoService';
import { ProductsFinanceHubContext } from './ProductsFinanceHubContext';
import type { HubGridRow } from '../../../../@types/oneButtonType';
import {
    ObdMilestonesGrid,
    badgeLayoutClassForCode,
    buildMilestoneBadgeGroups,
    partnerMilestoneIcon,
    useStandardMilestoneCards,
    type ObdMilestoneCardDef,
} from './sections/obdMilestonesShared';
import { mapQuoteProductTypeLongNameToCode } from './contractFlowHelpers';
import {
    isDashboardCommerceReady,
} from './systemDesignMissingItems';
import { useSystemDesignMissingItems } from './useSystemDesignMissingItems';
import './oneButton-css/obdMilestonesShared.css';
import './oneButton-css/FinancePartnerChecklistSection.css';
import { useSunnyFocusedSectionOpen } from './useSunnyFocusedSectionOpen';

const DEFAULT_PARTNERS = ['LightReach', 'GoodLeap', 'IGS', 'Cash', 'Enfin'];

function financePartnerMatchKey(name: string): string {
    return String(name || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '');
}

/**
 * Aligns master-list / opportunity display names with keys used by the financing grid,
 * `financePartnerSyncStatus`, and `PARTNER_SYNC_CONFIG`.
 */
function toCanonicalPartnerSyncKey(masterText: string): string {
    const k = financePartnerMatchKey(masterText);
    if (!k) return masterText.trim();
    if (k.includes('lightreach')) return 'LightReach';
    if (k.includes('goodleap') || (k.includes('good') && k.includes('leap'))) return 'GoodLeap';
    if (k.startsWith('igs')) return 'IGS';
    if (k.includes('cash')) return 'Cash';
    if (k.includes('enfin')) return 'Enfin';
    if (k.includes('sunnova')) return 'Sunnova';
    if (k.includes('sunlight')) return 'Sunlight';
    return masterText.trim();
}

function opportunityDetailsNested(od: any) {
    return od?.OpportunityDetails ?? od?.opportunityDetails;
}

function getOpportunityProductRows(od: any): any[] {
    if (!od) return [];
    const n = opportunityDetailsNested(od);
    const pick = od.OpportunityProduct ?? od.opportunityProduct ?? n?.OpportunityProduct ?? n?.opportunityProduct;
    return Array.isArray(pick) ? pick : [];
}

function partnerNameFromOpportunityProduct(row: any): string {
    return String(
        row?.Finance_Partner_Display_Name ??
            row?.FinancePartnerDisplayName ??
            row?.Finance_Partner ??
            row?.finance_Partner ??
            row?.Finance_Partner_Code ??
            ''
    ).trim();
}

function addFinancePartnerKeys(set: Set<string>, raw: string) {
    const trimmed = String(raw || '').trim();
    if (!trimmed) return;
    const lk = trimmed.toLowerCase();
    if (lk.includes('select finance')) return;
    set.add(financePartnerMatchKey(trimmed));
    set.add(financePartnerMatchKey(toCanonicalPartnerSyncKey(trimmed)));
}

function addFinancePartnerKeysFromHubRows(set: Set<string>, hubRows: HubGridRow[] | undefined) {
    if (!hubRows?.length) return;
    for (const row of hubRows) {
        if (!row.activeFlag) continue;
        addFinancePartnerKeys(
            set,
            row.finance_Partner_Display_Name || row.finance_Partner || ''
        );
    }
}

/** Partners enabled on Products, Financing grid selection, or finance lead list. */
function collectEnabledFinancePartnerKeys(
    opportunityDetails: any,
    selectedFinanceCells: Set<string>,
    hubRows?: HubGridRow[]
): Set<string> {
    const set = new Set<string>();

    addFinancePartnerKeysFromHubRows(set, hubRows);

    for (const row of getOpportunityProductRows(opportunityDetails)) {
        if (row?.ActiveFlag === false || row?.activeFlag === false) continue;
        addFinancePartnerKeys(set, partnerNameFromOpportunityProduct(row));
    }

    const financeInfo =
        opportunityDetails?.OpportunityProductsFinanceInfo ??
        opportunityDetails?.opportunityProductsFinanceInfo ??
        opportunityDetailsNested(opportunityDetails)?.OpportunityProductsFinanceInfo ??
        [];
    if (Array.isArray(financeInfo)) {
        for (const row of financeInfo) {
            addFinancePartnerKeys(set, partnerNameFromOpportunityProduct(row));
        }
    }

    selectedFinanceCells.forEach((cellKey) => {
        const partner = cellKey.split('::')[0];
        addFinancePartnerKeys(set, partner);
    });

    const leadList =
        opportunityDetails?.FinancePartnerLeadList ??
        opportunityDetails?.financePartnerLeadListModels ??
        opportunityDetails?.financePartnerLeadList ??
        [];
    if (Array.isArray(leadList)) {
        for (const row of leadList) {
            addFinancePartnerKeys(set, row?.FinancePartnerName ?? row?.Finance_Partner_Name ?? '');
        }
    }

    return set;
}

function isPartnerEnabled(displayName: string, syncKey: string, enabledKeys: Set<string>): boolean {
    if (!displayName.trim() || enabledKeys.size === 0) return false;
    const candidates = [
        financePartnerMatchKey(displayName),
        financePartnerMatchKey(syncKey),
        financePartnerMatchKey(toCanonicalPartnerSyncKey(displayName)),
        financePartnerMatchKey(toCanonicalPartnerSyncKey(syncKey)),
    ];
    return candidates.some((key) => enabledKeys.has(key));
}

function resolvePartnerSyncStatus(
    syncKey: string,
    displayName: string,
    statusMap: Record<string, PartnerSyncChecklist | undefined>
): PartnerSyncChecklist | undefined {
    const direct = statusMap[syncKey] ?? statusMap[displayName];
    if (direct) return direct;
    const target = financePartnerMatchKey(syncKey);
    for (const key of Object.keys(statusMap)) {
        if (financePartnerMatchKey(key) === target) return statusMap[key];
    }
    return undefined;
}

/** IGS and LightReach use the automation sync popup and capsule checklist status. */
function isIgsOrLightReachSyncPartner(syncKey: string): boolean {
    const u = syncKey.toUpperCase();
    return u === 'IGS' || u === 'LIGHTREACH';
}

export const FinancePartnerChecklistSection: React.FC = () => {
    const productsHub = useContext(ProductsFinanceHubContext);
    const [sectionOpen, setSectionOpen] = useSunnyFocusedSectionOpen('partner-sync');
    const [partnerModal, setPartnerModal] = useState<string | null>(null);
    const [loanStatusList, setLoanStatusList] = useState<any[]>([]);
    const [savedProducts, setSavedProducts] = useState<any[]>([]);

    const theme = useOneButtonStore((s) => s.theme);
    const financePartnerSyncStatus = useOneButtonStore((s) => s.financePartnerSyncStatus);
    const financePartnersList = useOneButtonStore((s) => s.financePartnersList);
    const selectedFinanceCells = useOneButtonStore((s) => s.selectedFinanceCells);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const opportunityId = useOneButtonStore((s) => s.opportunityID);
    const rawStatuses = useOneButtonStore((s) => s.financePartnerRawSyncStatuses);
    const setSyncStatus = useOneButtonStore((s) => s.setFinancePartnerSyncStatus);
    const setFinancePartnerRawSyncStatuses = useOneButtonStore((s) => s.setFinancePartnerRawSyncStatuses);
    const setContractCreationStatuses = useOneButtonStore((s) => s.setContractCreationStatuses);
    const financeSelectionRefreshTrigger = useOneButtonStore((s) => s.financeSelectionRefreshTrigger);
    const systemDesignMissingItems = useSystemDesignMissingItems();

    // Fetch initial sync status from API on mount / when opportunityId becomes available or when product settings are saved
    useEffect(() => {
        const fetchInitialStatus = async () => {
            if (!opportunityId) return;
            try {
                const res = await quoteServices.getSyncStatusList(String(opportunityId));
                const syncList = res?.FinancePartnerSyncStatuses || (Array.isArray(res) ? res : []);
                if (Array.isArray(syncList) && syncList.length > 0) {
                    setFinancePartnerRawSyncStatuses(syncList);
                }
                const contractStatuses = res?.ContractCreationStatuses || (Array.isArray(res) ? res : []);
                setContractCreationStatuses(Array.isArray(contractStatuses) ? contractStatuses : []);
                const ls = res?.LoanStatus || [];
                setLoanStatusList(ls);
            } catch (err) {
                console.error("Failed to fetch initial sync status in checklist section", err);
            }

            try {
                const productsRes = await opportunityInfoServices.retrieveOpportunityDetails(String(opportunityId));
                let productsArray: any[] = [];
                if (Array.isArray(productsRes)) {
                    productsArray = productsRes;
                } else if (productsRes && typeof productsRes === 'object') {
                    if (Array.isArray(productsRes.opportunityProduct)) {
                        productsArray = productsRes.opportunityProduct;
                    } else if (Array.isArray(productsRes.OpportunityProduct)) {
                        productsArray = productsRes.OpportunityProduct;
                    } else if (Array.isArray(productsRes.opportunityProductList)) {
                        productsArray = productsRes.opportunityProductList;
                    } else {
                        productsArray = Array.isArray(productsRes.data) ? productsRes.data : [];
                    }
                }
                setSavedProducts(productsArray);
            } catch (err) {
                console.error("Failed to fetch saved products in checklist section", err);
            }
        };

        fetchInitialStatus();
    }, [opportunityId, setFinancePartnerRawSyncStatuses, financeSelectionRefreshTrigger]);

    // Keep the checklist UI in sync with the raw statuses from the store/API for LightReach and IGS
    useEffect(() => {
        if (Array.isArray(rawStatuses) && rawStatuses.length > 0) {
            const statusObj = rawStatuses[0];
            const newStatus: Partial<PartnerSyncChecklist> = {
                lead: statusObj.IsLeadCreate === true,
                contact: statusObj.IsContactSync === true,
                titleCheck: (statusObj.IsTilte === true || statusObj.IsTitle === true),
                utility: statusObj.IsUtility === true,
                leadSystem: statusObj.IsLeadSystem === true,
                creditCheck: statusObj.IsCreditPass === true,
            };
            setSyncStatus('IGS', newStatus);
            setSyncStatus('LightReach', newStatus);
        }
    }, [rawStatuses, setSyncStatus]);

    const partnerCapsules = useMemo(() => {
        const fromMaster = (financePartnersList || [])
            .map((p: any) => String(p?.Text ?? '').trim())
            .filter(Boolean);
        const displayNames = fromMaster.length > 0 ? fromMaster : DEFAULT_PARTNERS;
        const seen = new Set<string>();
        return displayNames
            .filter((name) => {
                const key = financePartnerMatchKey(name);
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            })
            .map((displayName) => ({
                displayName,
                syncKey: toCanonicalPartnerSyncKey(displayName),
            }));
    }, [financePartnersList]);

    const enabledPartnerKeys = useMemo(
        () =>
            collectEnabledFinancePartnerKeys(
                opportunityDetails,
                selectedFinanceCells,
                productsHub?.rows
            ),
        [opportunityDetails, selectedFinanceCells, productsHub?.rows]
    );

    const visiblePartnerCapsules = useMemo(
        () =>
            partnerCapsules.filter(({ displayName, syncKey }) =>
                isPartnerEnabled(displayName, syncKey, enabledPartnerKeys)
            ),
        [partnerCapsules, enabledPartnerKeys]
    );

    const financeLeadIdList = useMemo(() => {
        return (
            opportunityDetails?.FinancePartnerLeadList ??
            opportunityDetails?.financePartnerLeadListModels ??
            opportunityDetails?.financePartnerLeadList ??
            []
        );
    }, [opportunityDetails]);

    const visibleFinanceLeads = useMemo(
        () =>
            (Array.isArray(financeLeadIdList) ? financeLeadIdList : []).filter((lead: any) =>
                Boolean(String(lead?.Finance_Partner_Sync_LeadID ?? '').trim()),
            ),
        [financeLeadIdList],
    );

    const salesForceId =
        opportunityDetails?.SalesForce_Id ??
        (typeof window !== 'undefined' ? sessionStorage.getItem('SalesForceId') : null) ??
        null;

    const standardMilestoneCards = useStandardMilestoneCards({
        opportunityNumericId: opportunityId,
        salesForceId,
        savedProducts,
        financePartnerSyncStatus,
    });

    const partnerMilestoneCards = useMemo((): ObdMilestoneCardDef[] => {
        return visiblePartnerCapsules.map(({ displayName, syncKey }) => {
            const status = resolvePartnerSyncStatus(syncKey, displayName, financePartnerSyncStatus);
            const showCapsuleSyncStatus = isIgsOrLightReachSyncPartner(syncKey);
            let complete =
                showCapsuleSyncStatus && isPartnerSyncCompleteForConfig(syncKey, status);
            let sublabel = showCapsuleSyncStatus ? (complete ? 'Completed' : 'Pending') : 'Finance partner';
            const clickable = isIgsOrLightReachSyncPartner(syncKey);
            const designBlocked = !isDashboardCommerceReady(systemDesignMissingItems);

            let badges: React.ReactNode = undefined;

            const isGoodLeap = syncKey === 'GoodLeap';
            const isEnfin = syncKey === 'Enfin';
            const isSunlight = syncKey === 'Sunlight';

            if (isGoodLeap || isEnfin || isSunlight) {
                const partnerProducts = savedProducts.filter((p: any) => {
                    const fpName = String(p.Finance_Partner || p.Finance_Partner_Display_Name || '').toLowerCase();
                    if (isGoodLeap && fpName.includes('good') && fpName.includes('leap')) return true;
                    if (isEnfin && fpName.includes('enfin')) return true;
                    if (isSunlight && fpName.includes('sunlight')) return true;
                    return false;
                });

                if (partnerProducts.length > 0) {
                    const groupedProducts = buildMilestoneBadgeGroups(partnerProducts);
                    let allGreen = true;
                    badges = groupedProducts.map((group, i: number) => {
                        const inLoanStatus = loanStatusList.some((ls: any) => {
                           const lsFpName = String(ls.financepartner || ls.FinancePartner || '').toLowerCase();
                           
                           let isFpMatch = false;
                           if (isGoodLeap && lsFpName.includes('good') && lsFpName.includes('leap')) isFpMatch = true;
                           if (isEnfin && lsFpName.includes('enfin')) isFpMatch = true;
                           if (isSunlight && lsFpName.includes('sunlight')) isFpMatch = true;
                           
                           if (!isFpMatch) return false;
                           
                           const lsProdCode = String(
                                mapQuoteProductTypeLongNameToCode(String(ls.productType || ls.ProductType || '')) ||
                                ls.productType ||
                                ls.ProductType ||
                                '',
                            )
                                .trim()
                                .toUpperCase();

                           return lsProdCode === group.code;
                        });

                        if (!inLoanStatus) allGreen = false;

                        const color = inLoanStatus ? 'green' : 'orange';
                        const statusTooltip = inLoanStatus ? 'Completed' : 'Pending';
                        
                        return (
                            <span
                                key={i}
                                title={statusTooltip}
                                className={`obd-v3-milestone-card__prod-badge ${badgeLayoutClassForCode(group.code)}`.trim()}>
                                {group.label}
                                <span className={`obd-v3-milestone-card__prod-indicator obd-v3-milestone-card__prod-indicator--${color}`} />
                            </span>
                        );
                    });

                    complete = allGreen;
                    sublabel = allGreen ? 'Completed' : 'Pending';
                } else {
                    const hasLead = visibleFinanceLeads.some((lead: any) => {
                        const fpName = String(lead.FinancePartnerName || '').toLowerCase();
                        if (isGoodLeap && fpName.includes('good') && fpName.includes('leap')) return true;
                        if (isEnfin && fpName.includes('enfin')) return true;
                        if (isSunlight && fpName.includes('sunlight')) return true;
                        return false;
                    });
                    if (hasLead) {
                        complete = true;
                        sublabel = 'Submitted';
                    }
                }
            }

            if (designBlocked) {
                sublabel = 'Steps required';
            }

            return {
                id: `partner-${financePartnerMatchKey(displayName)}`,
                label: displayName,
                sublabel,
                complete: showCapsuleSyncStatus ? complete : ((isGoodLeap || isEnfin || isSunlight) ? complete : false),
                icon: partnerMilestoneIcon(displayName),
                badges,
                disabled: designBlocked,
                onClick: () => {
                    if (designBlocked || !clickable) return;
                    setPartnerModal(syncKey);
                },
            };
        });
    }, [
        visiblePartnerCapsules,
        financePartnerSyncStatus,
        visibleFinanceLeads,
        loanStatusList,
        savedProducts,
        productsHub?.rows,
        systemDesignMissingItems,
    ]);

    const allMilestoneCards = useMemo(
        () => [...partnerMilestoneCards, ...standardMilestoneCards],
        [partnerMilestoneCards, standardMilestoneCards]
    );

    // Detail View Handlers
    const detailViewPage = (id: string, partner: string) => {
        let url = '';
        if (partner === 'SalesForce') {
            const base = (import.meta.env.VITE_OPPORTUNITY_DETAIL_URL || '').trim();
            url = `${base}${id}${id.endsWith('/view') ? '' : '/view'}`;
        } else if (partner === COMMON_CONSTANTS.SunnovaPartnerName) {
            const base = (import.meta.env.VITE_FINANCE_PARTNER_DETAIL_URL || '').trim();
            url = `${base}${id}`;
        }
        if (url) window.open(url, "_blank");
    };

    const igsDetailViewPage = (id: string) => {
        const base = (import.meta.env.VITE_IGS_FINANCE_PARTNER_DETAIL_URL || '').trim();
        const url = `${base}${id}/overview`;
        window.open(url, "_blank");
    };

    const lightreachDetailViewPage = (id: string) => {
        const base = (import.meta.env.VITE_LIGHTREACH_FINANCE_PARTNER_DETAIL_URL || '').trim();
        const url = `${base}${id}`;
        window.open(url, "_blank");
    };

    const goodLeapDetailViewPage = (id: string, method: string) => {
        let url = '';
        if (method?.includes('Loan')) {
            const base = (import.meta.env.VITE_GOODLEAP_LOAN_URL || '').trim();
            url = `${base}${id}`;
        } else if (method?.includes('Lease') || method?.includes('PPA')) {
            const base = (import.meta.env.VITE_GOODLEAP_TPO_URL || '').trim();
            url = `${base}${id}`;
        }
        if (url) window.open(url, "_blank");
    };

    const enfinDetailViewPage = () => {
        let enfinUrl = (import.meta.env.VITE_ENFIN_FINANCE_PRTNER_DETAIL_URL || '').trim();
        window.open(enfinUrl, "_blank");
    }

    const copyToClipboard = (e: React.MouseEvent, text: string) => {
        e.stopPropagation();
        navigator.clipboard.writeText(text);
        message.success({
            content: `Copied ${text}`,
            className: `obd-message obd-theme-${theme}`,
            duration: 2,
        });
    };


    return (
        <>
            <div
                className={`obd-card obd-card--partner-checklist obd-theme-${theme}`}
                data-obd-guide="partner-sync"
            >
                <div
                    role="button"
                    tabIndex={0}
                    className="obd-quotes__header"
                    onClick={() => setSectionOpen((o: boolean) => !o)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSectionOpen((o: boolean) => !o);
                        }
                    }}
                >
                    <span className="obd-card__label obd-card__label--section-icon">
                        <span className="obd-section-head-icon obd-section-head-icon--inline" aria-hidden>
                            <Buildings size={16} weight="duotone" />
                        </span>
                        Status &amp; Milestones
                    </span>
                    <div className="obd-financing__header-actions">
                        <CaretDown
                            size={14}
                            weight="bold"
                            className={`obd-quotes__chevron ${sectionOpen ? 'obd-quotes__chevron--open' : ''}`}
                        />
                    </div>
                </div>
                {sectionOpen && (
                    <div className="obd-partner-checklist__body obd-partner-checklist__body--milestones">
                        <ObdMilestonesGrid theme={theme} cards={allMilestoneCards} />

                        {visibleFinanceLeads.length > 0 ? (
                            <div className="obd-partner-lead-ids">
                                <div className="obd-partner-lead-ids__header">
                                    <span className="obd-partner-lead-ids__title">Partner lead IDs</span>
                                    <span className="obd-partner-lead-ids__divider" aria-hidden />
                                </div>
                                <div className="obd-partner-lead-ids__grid">
                                    {visibleFinanceLeads.map((financeLead: any, idx: number) => {
                                        const fpName = (financeLead.FinancePartnerName || '').toLowerCase();
                                        const isSunnova = fpName.includes('sunnova');
                                        const isIGS = fpName.includes('igs');
                                        const isLightreach = fpName.includes('lightreach');
                                        const isSunlight = fpName.includes('sunlight');
                                        const isGoodLeap = fpName.includes('good') && fpName.includes('leap');
                                        const isEnfin = fpName.includes('enfin');

                                        return (
                                            <div key={idx} className="obd-lead-chip">
                                                <div className="obd-lead-chip__top">
                                                    <div className="obd-lead-chip__type-wrap">
                                                        {financeLead.ProductTypeName?.toLowerCase().includes('solar') && <Sun size={14} weight="duotone" className="obd-lead-chip__type-icon" />}
                                                        {financeLead.ProductTypeName?.toLowerCase().includes('battery') && <BatteryHigh size={14} weight="duotone" className="obd-lead-chip__type-icon" />}
                                                        {(financeLead.ProductTypeName?.toLowerCase().includes('roof') || financeLead.ProductTypeName?.toLowerCase().includes('rr')) && <House size={14} weight="duotone" className="obd-lead-chip__type-icon" />}
                                                        <span className="obd-lead-chip__type-name">{financeLead.ProductTypeName}</span>
                                                    </div>
                                                    <span className="obd-lead-chip__partner-name">{financeLead.FinancePartnerName}</span>
                                                </div>
                                                <div className="obd-lead-chip__bottom">
                                                    <div className="obd-lead-chip__id-group">
                                                        <Link size={12} weight="bold" className="obd-lead-chip__link-icon" />
                                                        {isSunnova && (
                                                            <a
                                                                className="obd-lead-chip__link"
                                                                onClick={() => detailViewPage(financeLead.Finance_Partner_Sync_LeadID, COMMON_CONSTANTS.SunnovaPartnerName)}
                                                            >
                                                                {financeLead.Finance_Partner_Sync_LeadID}
                                                            </a>
                                                        )}
                                                        {isIGS && (
                                                            <a
                                                                className="obd-lead-chip__link"
                                                                onClick={() => igsDetailViewPage(financeLead.Finance_Partner_Sync_LeadID)}
                                                            >
                                                                {financeLead.Finance_Partner_Sync_LeadID}
                                                            </a>
                                                        )}
                                                        {isLightreach && (
                                                            <a
                                                                className="obd-lead-chip__link"
                                                                onClick={() => lightreachDetailViewPage(financeLead.Finance_Partner_Sync_LeadID)}
                                                            >
                                                                {financeLead.Finance_Partner_Sync_LeadID}
                                                            </a>
                                                        )}
                                                        {isSunlight && (
                                                            <span className="obd-lead-chip__id-text">
                                                                {financeLead.Finance_Partner_Sync_LeadID}
                                                            </span>
                                                        )}
                                                        {isGoodLeap && (
                                                            <a
                                                                className="obd-lead-chip__link"
                                                                onClick={() => goodLeapDetailViewPage(financeLead.Finance_Partner_Sync_LeadID, financeLead.FinanceMethodName)}
                                                            >
                                                                {financeLead.Finance_Partner_Sync_LeadID}
                                                                <span className="obd-lead-chip__method-badge">{financeLead.FinanceMethodName}</span>
                                                            </a>
                                                        )}
                                                        {isEnfin && financeLead.ProductTypeName == 'Solar' && (
                                                            <a className="obd-lead-chip__link"
                                                                onClick={() => enfinDetailViewPage()}>
                                                                { 
                                                                    ' (' + financeLead.FinanceMethodName + ') - ' + financeLead.Finance_Partner_Sync_LeadID
                                                                }
                                                          </a>
                                                        )}
                                                        {!isSunnova && !isIGS && !isLightreach && !isSunlight && !isGoodLeap && !isEnfin && (
                                                            <span className="obd-lead-chip__id-text">
                                                                {financeLead.Finance_Partner_Sync_LeadID}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="obd-lead-chip__copy-btn"
                                                        onClick={(e) => copyToClipboard(e, financeLead.Finance_Partner_Sync_LeadID)}
                                                        title="Copy Lead ID"
                                                    >
                                                        <Copy size={14} weight="regular" />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : null}
                    </div>
                )}
            </div>

            {partnerModal && (
                <FinancePartnerChecklistModal
                    open={!!partnerModal}
                    partnerName={partnerModal}
                    showProductTypesInTitle
                    onClose={() => setPartnerModal(null)}
                />
            )}

        </>
    );
};
